import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useClientConfig } from "@/lib/hooks";
import { isMobile } from "@/lib/config";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise: Promise<void> | null = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Turnstile could not be loaded"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface TurnstileHandle {
  /** Tokens are single use: reset after every submit. */
  reset: () => void;
}

/**
 * Cloudflare Turnstile widget (same behaviour as the Blazor TurnstileWidget): renders nothing when the API reports
 * Turnstile as disabled (GET api/v1/config) and never in the mobile build. The token goes into the
 * X-Turnstile-Token header.
 */
export const Turnstile = forwardRef<
  TurnstileHandle,
  { onToken: (token: string | null) => void; onEnabled?: (enabled: boolean) => void }
>(function Turnstile({ onToken, onEnabled }, ref) {
  const { data: config } = useClientConfig();
  const enabled = !isMobile && !!config?.turnstileEnabled && !!config.turnstileSiteKey;
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => onEnabled?.(enabled), [enabled, onEnabled]);

  useImperativeHandle(ref, () => ({
    reset() {
      onTokenRef.current(null);
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    },
  }));

  useEffect(() => {
    if (!enabled || !config?.turnstileSiteKey) return;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: config.turnstileSiteKey!,
          language: "en",
          theme: "light",
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
        });
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [enabled, config?.turnstileSiteKey]);

  if (!enabled) return null;
  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={container} />
      {failed && (
        <p className="text-xs text-destructive">
          The security check could not be loaded. Disable blockers and reload the page.
        </p>
      )}
    </div>
  );
});

/** State helper: `missing` is true while Turnstile is on and no token was issued yet. */
export function useTurnstileState() {
  const [token, setToken] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const ref = useRef<TurnstileHandle>(null);
  return {
    ref,
    token,
    setToken,
    setEnabled,
    missing: enabled && !token,
    reset: () => ref.current?.reset(),
  };
}
